import { forwardRef, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { CountrySelect } from './countrySelect';

const INPUT_PATTERN = /\d/;

interface PhoneInputProps {
  // COMMON PROPS
  className?: string;
  caption?: ReactNode;
  error?: ReactNode;
  // BASE INPUT PROPS
  name?: string;
  formStyle?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  value?: { code: string; number: string };
  onChange?: (value: { code: string; number: string }) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>((props, ref) => {
  const keyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.altKey) return;
    if (e.key && e.key.length === 1) {
      if (!e.key.match(INPUT_PATTERN)) e.preventDefault();
    }
  };

  return (
    <div className='w-full'>
      <div className='mb-[6px]'>
        {props.required && <span className='pr-2 text-xl'>*</span>}
        <label
          className={clsx(
            'text-[18px] font-light tracking-[-0.1px] leading-[25px] ',
            !props.formStyle && (PaletteUtils.isBordierPalette ? 'text-[#303034] font-semibold' : 'text-[#dadada] !font-light'),
            props.formStyle && (PaletteUtils.isBordierPalette ? '' : 'text-[#4c4c52] !font-bold mb-2'),
          )}
        >
          {props.caption}
        </label>
        {props.required && <span className='hidden'>*</span>}
      </div>
      <div className='flex flex-nowrap'>
        <CountrySelect
          name={props.name}
          required={props.required}
          value={props.value?.code}
          onChange={(code) => props.onChange?.({ ...props.value!, code })}
          formStyle={props.formStyle}
          error={props.error}
        />
        <input
          ref={ref}
          className={clsx(
            'transaction-all duration-300',
            props.formStyle
              ? 'w-full !h-10 px-[15px] tracking-[0.3px] !bg-[#fcfcfc] border border-[#aaaab2] shadow-[inset_0_2px_6px_0_rgba(85,85,90,0.39)] text-[#4c4c52] text-lg font-semibold'
              : 'w-full h-[50px] px-[15px] text-[18px] font-semibold tracking-[0.3px]  border  focus:border-[#979797] focus:outline-none',
            !props.formStyle &&
              (PaletteUtils.isBordierPalette
                ? 'bg-[#fcfcfc] border-[#e1e1e1]'
                : 'bg-[#303034] border-[#6d6d75] hover:!border-[#9f9fa5] focus:!border-[#9f9fa5] text-[#dadada]'),
            {
              '!border-[#d34014]': !!props.error,
              '!hover:border-[#979797]': !props.error,
              '!opacity-50 !cursor-not-allowed': props.disabled || props.readOnly,
            },
            props.className,
          )}
          name={`${props.name}_number`}
          type='tel'
          placeholder={props.placeholder}
          required={props.required}
          readOnly={props.readOnly}
          maxLength={props.maxLength || 13}
          value={props.value?.number}
          onChange={(e) => props.onChange?.({ ...props.value!, number: e.target.value })}
          onKeyDown={keyDownHandler}
        />
      </div>
      {props.error && (
        <div
          className={clsx(
            'left-0 mt-0 top-full text-[#d44112] tracking-[-0.1px] text-left whitespace-pre-line',
            props.formStyle && 'text-base font-normal absolute top-0',
            !props.formStyle && 'text-lg mt-[9px] leading-6',
          )}
        >
          {props.error}
        </div>
      )}
    </div>
  );
});
